import useClassNames from '@medflex/hooks/useClassNames';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { IconMic } from '../Icons';
import Tooltip from '../Tooltip';
import Button from '../Button';

const Trigger = ({
  onStart,
  onStop,
  onHandleAudioPermission,
  disabled,
  delay,
  tooltip,
  className,
  audioPermissionGranted,
}) => {
  const [pressing, setPressing] = useState(false);
  const [recording, setRecording] = useState(false);
  const isWideScreen = useIsWideScreen();

  const cn = useClassNames(
    () => ['flex', 'justify-center', 'text-slate-300', className],
    [className, pressing],
  );

  const handleClick = async () => {
    if (!audioPermissionGranted) {
      onHandleAudioPermission();
      return;
    }

    if (isWideScreen) {
      if (recording) {
        setRecording(false);
        onStop();
      } else {
        setRecording(true);
        onStart();
      }
    }
  };

  const handlePressStarted = () => {
    if (!isWideScreen && !disabled) setPressing(true);
  };

  const handlePressStopped = () => {
    if (!isWideScreen) setPressing(false);
  };

  useEffect(() => {
    if (pressing && !disabled) {
      const timeout = setTimeout(() => {
        setPressing(true);
        onStart();
      }, delay);

      return () => {
        setPressing(false);
        onStop();
        clearTimeout(timeout);
      };
    }

    return undefined;
  }, [pressing, disabled, delay]);

  const buttonClasses = useClassNames(
    () => [
      'border-none',
      'px-0',
      'h-auto',
      'leading-none',
      'focus:outline-none',
      disabled && 'pointer-events-none cursor-not-allowed',
    ],
    [disabled],
  );

  return (
    <div data-component="VoiceMessage__Trigger" className={cn}>
      {tooltip ? (
        <Tooltip title={tooltip}>
          <Button
            kind="primary:ghost"
            shape="square"
            className={buttonClasses}
            onClick={handleClick}
            onMouseDown={handlePressStarted}
            onMouseUp={handlePressStopped}
            onTouchStart={handlePressStarted}
            onTouchEnd={handlePressStopped}
            onTouchCancel={handlePressStopped}
            onBlur={handlePressStopped}
            disabled={disabled}
          >
            <IconMic size="6" />
          </Button>
        </Tooltip>
      ) : (
        <Button
          kind="primary:ghost"
          shape="square"
          className={buttonClasses}
          onClick={handleClick}
          onMouseDown={handlePressStarted}
          onMouseUp={handlePressStopped}
          onTouchStart={handlePressStarted}
          onTouchEnd={handlePressStopped}
          onTouchCancel={handlePressStopped}
          onBlur={handlePressStopped}
          disabled={disabled}
        >
          <IconMic size="6" />
        </Button>
      )}
    </div>
  );
};

Trigger.propTypes = {
  onStart: PropTypes.func,
  onStop: PropTypes.func,
  onHandleAudioPermission: PropTypes.func,
  delay: PropTypes.number,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  disabled: PropTypes.bool,
  audioPermissionGranted: PropTypes.bool,
  tooltip: PropTypes.string,
};

Trigger.defaultProps = {
  onStart: () => {},
  onStop: () => {},
  onHandleAudioPermission: () => {},
  delay: 500,
  disabled: false,
  tooltip: null,
  audioPermissionGranted: false,
};

export default Trigger;
